import { useCallback } from 'react';
import { camelize } from 'src/utils/camelize';

import { usePermissionCheck } from '../shared/usePermissionCheck';

export const usePermissions = () => {
  let permission = usePermissionCheck();

  /**
   * @typedef { object } Args
   * @prop { keyof Permission } [can]
   * @prop { keyof Permission } [cannot]
   * @prop { object } [object]
   */
  /** @type { (args: Args) => boolean } */
  const permissionCheck = useCallback(
    ({ can, cannot, object }) => {
      const check = can || cannot;
      let hasPermission =
        check &&
        (object
          ? permission[`can${camelize(check, { pascalCase: true })}`](object)
          : permission[`can${camelize(check, { pascalCase: true })}`]);

      if (cannot) {
        hasPermission = !hasPermission;
      }

      return hasPermission;
    },
    [permission]
  );

  return { permissionCheck };
};

/**
  @typedef { object } Permission
  @prop { string } editLogEntries
  @prop { string } viewTasks
  @prop { string } viewOtherTimeClock
  @prop { string } clockIn
  @prop { string } viewStaffUserMenu
  @prop { string } viewTimeTracking
  @prop { string } viewApiKeys
  @prop { string } viewWebhooks
  @prop { string } exportTimeClockReport
  @prop { string } viewAppointmentMembers
  @prop { string } editAppointmentMembers
  @prop { string } checkInAppointment
  @prop { string } globalCheckIn
  @prop { string } viewOthersScheduling
  @prop { string } editNoteFields
  @prop { string } viewBillingInfo
  @prop { string } viewMessages
*/

/**
 * @typedef { object } Props
 * @prop { React.ReactNode } children
 * @prop { React.ReactNode } [noPermission]
 * @prop { keyof Permission } [can]
 * @prop { keyof Permission } [cannot]
 * @prop { object } [object]
 */

/** @type { React.FC<Props> } */
export const PermissionCheck = ({
  can,
  cannot,
  object,
  children,
  noPermission,
}) => {
  let { permissionCheck } = usePermissions();
  let hasPermission = permissionCheck({ can, cannot, object });

  if (!hasPermission) {
    return <>{noPermission}</> || null;
  }

  return <>{children}</>;
};
